/**
 * LeadDashboardOverview — /leads-dashboard
 *
 * Landing page for the "Leads Dashboard" sidebar group. Summarizes
 * the four pages under it (Direct Sales, Indirect Sales, Brokerage
 * Hub, Lead Lifecycle) so a user can pick which area to open. Each
 * card shows a short description, a couple of live counts, and a
 * button to open that page.
 *
 * Deliberately does NOT duplicate the detail pages — it's a pure
 * navigation + at-a-glance overview. Fetches use small endpoints
 * already used elsewhere in the app.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  User, Share2, Building2, Activity, ArrowRight,
  Folder, RefreshCw, TrendingUp,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function LeadDashboardOverview() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [counts, setCounts] = useState({
    directLeads: null,
    indirectLeads: null,
    brokerages: null,
    brokerageLeads: null,
    lifecycleStages: null,
  });
  const [loading, setLoading] = useState(true);

  const fetchCounts = React.useCallback(async () => {
    setLoading(true);
    // Fire everything in parallel; ignore individual failures so one
    // slow / unavailable endpoint doesn't blank the whole page.
    const settle = (p) => p.then((r) => r).catch(() => null);
    const [
      direct, indirect, brokerages, brokerageLeads, lifecycle,
    ] = await Promise.all([
      settle(axios.get(`${API}/api/contacts`, { params: { token, source: 'direct', limit: 1 } })),
      settle(axios.get(`${API}/api/contacts`, { params: { token, source: 'indirect', limit: 1 } })),
      settle(axios.get(`${API}/api/brokerage-hub/brokerages`, { params: { token } })),
      settle(axios.get(`${API}/api/brokerage-hub/leads-summary`, { params: { token } })),
      settle(axios.get(`${API}/api/lifecycle-stages`, { params: { token } })),
    ]);
    setCounts({
      directLeads:    direct?.data?.total ?? direct?.data?.count ?? null,
      indirectLeads:  indirect?.data?.total ?? indirect?.data?.count ?? null,
      brokerages:     brokerages?.data?.data?.count ?? brokerages?.data?.count ?? null,
      brokerageLeads: brokerageLeads?.data?.data?.total_leads ?? brokerageLeads?.data?.total ?? null,
      lifecycleStages: Array.isArray(lifecycle?.data?.stages)
        ? lifecycle.data.stages.length
        : (Array.isArray(lifecycle?.data) ? lifecycle.data.length : null),
    });
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchCounts(); }, [fetchCounts]);

  const cards = [
    {
      key: 'direct',
      title: 'Direct Sales',
      description: 'Leads generated by your own sales agents from marketing campaigns, referrals, and walk-ins.',
      icon: User,
      iconTone: 'bg-blue-100 text-blue-700',
      accent: 'border-blue-200',
      route: '/leads-dashboard/direct-sales',
      stats: [
        { label: 'Total leads', value: fmt(counts.directLeads) },
      ],
    },
    {
      key: 'indirect',
      title: 'Indirect Sales',
      description: 'Leads referred by external partners (BDMs, indirect_sales users, third parties). Attribution flows through the referrer.',
      icon: Share2,
      iconTone: 'bg-indigo-100 text-indigo-700',
      accent: 'border-indigo-200',
      route: '/leads-dashboard/indirect-sales',
      stats: [
        { label: 'Total leads', value: fmt(counts.indirectLeads) },
      ],
    },
    {
      key: 'brokerage',
      title: 'Brokerage Hub',
      description: 'External brokerage partner firms and every lead they source. BDMs manage the partners; commissions and MoUs live here.',
      icon: Building2,
      iconTone: 'bg-green-100 text-green-700',
      accent: 'border-green-200',
      route: '/leads-dashboard/brokerage-hub',
      stats: [
        { label: 'Brokerages',    value: fmt(counts.brokerages) },
        { label: 'Broker leads',  value: fmt(counts.brokerageLeads) },
      ],
    },
    {
      key: 'lifecycle',
      title: 'Lead Lifecycle',
      description: 'End-to-end journey of every lead across stages — new, qualified, site visit, negotiation, booking, closed.',
      icon: Activity,
      iconTone: 'bg-violet-100 text-violet-700',
      accent: 'border-violet-200',
      route: '/lead-lifecycle',
      stats: [
        { label: 'Stages configured', value: fmt(counts.lifecycleStages) },
      ],
    },
  ];

  return (
    <div className="p-6 space-y-6" data-testid="lead-dashboard-overview">
      {/* Header */}
      <div className="flex items-start gap-4">
        <div className="w-14 h-14 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center">
          <Folder className="h-7 w-7" />
        </div>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-slate-900">Leads Dashboard</h1>
          <p className="text-sm text-slate-500 mt-1">
            All four lead flows in one place — Direct Sales, Indirect Sales,
            Brokerage Hub, and Lead Lifecycle. Pick a card to jump into a
            detail page.
          </p>
        </div>
        <Button variant="outline" onClick={fetchCounts} disabled={loading}>
          <RefreshCw size={14} className={`mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      {/* 4 area cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {cards.map((c) => (
          <Card key={c.key} className={`transition-all shadow-sm hover:shadow-md ${c.accent}`}>
            <CardHeader className="border-b">
              <div className="flex items-center gap-3">
                <div className={`w-11 h-11 rounded-lg flex items-center justify-center ${c.iconTone}`}>
                  <c.icon size={20} />
                </div>
                <div className="flex-1">
                  <CardTitle className="text-lg text-slate-900">{c.title}</CardTitle>
                  <CardDescription className="mt-0.5">{c.description}</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-5 space-y-4">
              {/* Stats row */}
              <div className="grid grid-cols-2 gap-3">
                {c.stats.map((s) => (
                  <div key={s.label} className="rounded-lg border bg-slate-50/70 px-3 py-2">
                    <div className="text-xs text-slate-500 uppercase tracking-wide">{s.label}</div>
                    <div className="text-2xl font-bold text-slate-900 mt-0.5">
                      {loading && s.value === '—' ? '…' : s.value}
                    </div>
                  </div>
                ))}
              </div>

              {/* Action */}
              <Button
                onClick={() => navigate(c.route)}
                className="w-full"
              >
                Open {c.title}
                <ArrowRight size={14} className="ml-2" />
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Footer hint */}
      <Card className="border-dashed">
        <CardContent className="p-4 flex items-center gap-3">
          <TrendingUp className="text-slate-400" size={18} />
          <p className="text-xs text-slate-500">
            Counts come from lightweight summary endpoints. If a number reads "—",
            the endpoint didn't return a count field yet — the detail page
            still shows the full data.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

// -----------------------------------------------------------

function fmt(n) {
  if (n === null || n === undefined) return '—';
  if (typeof n !== 'number' || Number.isNaN(n)) return '—';
  return new Intl.NumberFormat('en-US').format(n);
}
